import { Injectable } from '@angular/core';
import {
  ToastService,
  ActiveToast,
  ToastOptions,
  ToastPosition,
} from '@services/ui-services/toast.service';

const SUCCESS_TOAST_SETUP: any = {
  name: '',
  message: '',
  info: '',
  time: '',
  toastIcons: {
    success: 'check',
    avatar: 'type',
  },
  type: 'success',
  State: 'default',
  primaryButton: false,
  secondaryButton: false,
  showControls: false,
};

const ERROR_TOAST_SETUP: any = {
  name: '',
  message: '',
  info: '',
  time: '',
  toastIcons: {
    cancel: 'cancel',
    avatar: 'type',
  },
  type: 'cancel',
  State: 'default',
  primaryButton: false,
  secondaryButton: false,
  showControls: false,
};

export interface NotifyConfig {
  name?: string;
  type?: string;
  position?: ToastPosition;
  duration?: number;
  description?: string;
  toastIcons?: {
    error?: string;
    avatar?: string;
  };
  primaryButton?: {
    label: string;
    action: () => void;
    closeOnClick?: boolean;
  };
  useBuiltInToast?: boolean; // Flag to use ngx-sonner built-in toasts vs custom component
  showCloseButton?: boolean; // Show close button on custom toasts
}

/**
 * Enhanced NotifyService with ngx-sonner integration
 *
 * Features:
 * - Uses your custom ToastComponent by default (maintains your theme/styling)
 * - Optional rich colors with built-in ngx-sonner toasts
 * - Dynamic positioning (top-left, top-center, top-right, bottom-left, bottom-center, bottom-right)
 * - Multiple toast types (success, error, info, warning, loading)
 * - Promise toasts for async operations
 * - Action buttons and descriptions
 * - Close button functionality for custom toasts
 *
 * @example
 * // Basic usage with your custom styled component
 * this.notifyService.success('Order created successfully!');
 * this.notifyService.error('Failed to create order');
 *
 * // With custom position and close button
 * this.notifyService.success('Message', {
 *   position: 'top-right',
 *   showCloseButton: true
 * });
 *
 * // With description and action using your custom component
 * this.notifyService.success('Order created', {
 *   description: 'Order #12345 has been created successfully',
 *   primaryButton: {
 *     label: 'View Order',
 *     action: () => this.router.navigate(['/orders/12345'])
 *   },
 *   showCloseButton: true
 * });
 *
 * // Use built-in rich colors when needed
 * this.notifyService.success('Modern toast!', { useBuiltInToast: true });
 *
 * // Promise toast for async operations
 * this.notifyService.promise(
 *   this.orderService.createOrder(data),
 *   {
 *     loading: 'Creating order...',
 *     success: 'Order created successfully!',
 *     error: 'Failed to create order'
 *   }
 * );
 */
@Injectable({ providedIn: 'root' })
export class NotifyService {
  private activeToast: ActiveToast | null = null;

  constructor(private readonly toastService: ToastService) {}

  /**
   * Show success toast using your custom component (default) or built-in rich colors
   * @param message - The main message to display
   * @param config - Optional configuration for position, duration, actions, etc.
   */
  success(message: string, config?: NotifyConfig) {
    // Use built-in success toast with rich colors when explicitly requested
    if (config?.useBuiltInToast) {
      const toastOptions: ToastOptions = {
        position: config?.position || 'bottom-left',
        duration: config?.duration || 3000,
        description: config?.description,
        action: config?.primaryButton
          ? {
              label: config.primaryButton.label,
              onClick: () => {
                config.primaryButton?.action();
              },
            }
          : undefined,
        useCustomComponent: false,
        onDismiss: toast => {
          if (this.activeToast?.id === toast.id) {
            this.activeToast = null;
          }
        },
      };

      this.activeToast = this.toastService.showBuiltInToast(message, 'success', toastOptions);
    } else {
      // Use your custom component by default
      const params = {
        ...SUCCESS_TOAST_SETUP,
        ...(config || {}),
        message,
        showControls: true,
        primaryButton: config?.primaryButton
          ? {
              label: config.primaryButton.label,
              action: () => {
                config.primaryButton?.action();
                if (config.primaryButton?.closeOnClick !== false && this.activeToast) {
                  this.toastService.dismissToast(this.activeToast);
                }
              },
            }
          : false,
        secondaryButton: false,
      };

      const toastOptions: ToastOptions = {
        position: config?.position || 'bottom-left',
        duration: config?.duration || 3000,
        showCloseButton: config?.showCloseButton !== false, // Show close button by default
      };

      this.activeToast = this.toastService.showCustomToast(params, toastOptions);
    }
  }

  /**
   * Show error toast using your custom component (default) or built-in rich colors
   * @param message - The main error message to display
   * @param config - Optional configuration for position, duration, actions, etc.
   */
  error(message: string, config?: NotifyConfig) {
    // Use built-in error toast with rich colors when explicitly requested
    if (config?.useBuiltInToast) {
      const toastOptions: ToastOptions = {
        position: config?.position || 'bottom-left',
        duration: config?.duration || 3000,
        description: config?.description,
        action: config?.primaryButton
          ? {
              label: config.primaryButton.label,
              onClick: () => {
                config.primaryButton?.action();
              },
            }
          : undefined,
        useCustomComponent: false,
        onDismiss: toast => {
          if (this.activeToast?.id === toast.id) {
            this.activeToast = null;
          }
        },
      };

      this.activeToast = this.toastService.showBuiltInToast(message, 'error', toastOptions);
    } else {
      // Use your custom component by default
      const params = {
        ...ERROR_TOAST_SETUP,
        ...(config || {}),
        message,
        showControls: true,
        primaryButton: config?.primaryButton
          ? {
              label: config.primaryButton.label,
              action: () => {
                config.primaryButton?.action();
                if (config.primaryButton?.closeOnClick !== false && this.activeToast) {
                  this.toastService.dismissToast(this.activeToast);
                }
              },
            }
          : false,
        secondaryButton: false,
      };

      const toastOptions: ToastOptions = {
        position: config?.position || 'bottom-left',
        duration: config?.duration || 3000,
        showCloseButton: config?.showCloseButton !== false, // Show close button by default
      };

      this.activeToast = this.toastService.showCustomToast(params, toastOptions);
    }
  }

  /**
   * Show info toast - uses built-in ngx-sonner by default for this type
   * @param message - The info message to display
   * @param config - Optional configuration for position, duration, actions, etc.
   */
  info(message: string, config?: NotifyConfig) {
    const toastOptions: ToastOptions = {
      position: config?.position || 'bottom-left',
      duration: config?.duration || 3000,
      description: config?.description,
      action: config?.primaryButton
        ? {
            label: config.primaryButton.label,
            onClick: () => {
              config.primaryButton?.action();
            },
          }
        : undefined,
      useCustomComponent: config?.useBuiltInToast === false ? true : false, // Use built-in by default for info
      showCloseButton: config?.showCloseButton,
    };

    this.activeToast = this.toastService.showToast(message, 'info', toastOptions);
  }

  /**
   * Show warning toast - uses built-in ngx-sonner by default for this type
   * @param message - The warning message to display
   * @param config - Optional configuration for position, duration, actions, etc.
   */
  warning(message: string, config?: NotifyConfig) {
    const toastOptions: ToastOptions = {
      position: config?.position || 'bottom-left',
      duration: config?.duration || 3000,
      description: config?.description,
      action: config?.primaryButton
        ? {
            label: config.primaryButton.label,
            onClick: () => {
              config.primaryButton?.action();
            },
          }
        : undefined,
      useCustomComponent: config?.useBuiltInToast === false ? true : false, // Use built-in by default for warning
      showCloseButton: config?.showCloseButton,
    };

    this.activeToast = this.toastService.showToast(message, 'warning', toastOptions);
  }

  /**
   * Show loading toast - uses built-in ngx-sonner for spinner animation
   * @param message - The loading message to display
   * @param config - Optional configuration for position, duration, etc.
   * @returns ActiveToast - Reference to update or dismiss the loading toast
   */
  loading(message: string, config?: NotifyConfig) {
    const toastOptions: ToastOptions = {
      position: config?.position || 'bottom-left',
      duration: config?.duration || 0, // Loading toasts typically don't auto-dismiss
      useCustomComponent: false, // Always use built-in for loading (spinner animation)
    };

    this.activeToast = this.toastService.showBuiltInToast(message, 'loading', toastOptions);
    return this.activeToast;
  }

  /**
   * Show promise toast that updates based on promise state
   * @param promise - Promise or function returning a promise
   * @param options - Messages for different states and configuration
   */
  promise<T>(
    promise: Promise<T> | (() => Promise<T>),
    options: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
      position?: ToastPosition;
      duration?: number;
      useCustomComponent?: boolean;
    }
  ) {
    return this.toastService.promise(promise, {
      ...options,
      useCustomComponent: options.useCustomComponent !== true ? false : true, // Use built-in by default for promise complexity
    });
  }

  /**
   * Dismiss the current active toast
   */
  dismiss() {
    if (this.activeToast) {
      this.toastService.dismissToast(this.activeToast);
      this.activeToast = null;
    }
  }

  /**
   * Dismiss all visible toasts
   */
  dismissAll() {
    this.toastService.dismissAll();
    this.activeToast = null;
  }
}
